import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { INR } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, Ban, CheckCircle2 } from "lucide-react";
import Can from "@/components/Can";
import ExcelButton from "@/components/ExcelButton";
import { exportExcel, n, stamp } from "@/lib/excel";

const EMPTY = { name: "", phone: "", email: "", address: "", city: "", state: "", gstin: "", opening_balance: 0, credit_limit: 0 };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [balances, setBalances] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [sp, setSp] = useSearchParams();
  const q = sp.get("q") || "";
  const setQ = (val) => setSp(prev => { const n = new URLSearchParams(prev); val ? n.set("q", val) : n.delete("q"); return n; }, { replace: true });

  const load = async () => {
    const [c, r] = await Promise.all([api.get("/customers"), api.get("/reports/customer-balance")]);
    setItems(c.data);
    const b = {}; r.data.forEach((x) => (b[x.id] = x.balance));
    setBalances(b);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success(editId ? "Customer updated" : "Customer added");
      setOpen(false); setForm(EMPTY); setEditId(null);
      load();
    } catch (err) { toast.error(fmtError(err.response?.data?.detail)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete customer "${c.name}"? Only possible if they have no transactions.`)) return;
    try { await api.delete(`/customers/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(fmtError(err.response?.data?.detail), { duration: 7000 }); }
  };
  const toggleActive = async (c) => {
    const off = c.active !== false;
    if (off && !window.confirm(`Deactivate "${c.name}"?\n\nThey will no longer be selectable on new invoices, receipts or vouchers. Existing transactions and reports are unaffected.`)) return;
    await api.post(`/customers/${c.id}/${off ? "deactivate" : "activate"}`); toast.success(off ? "Customer deactivated" : "Customer reactivated"); load();
  };

  const filtered = items.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.phone.includes(q));
  const excel = () => exportExcel(`Customers-${stamp()}`, [{ name: "Customers", title: "Customer Master", subtitle: q ? `filter: ${q}` : "All customers",
    columns: [{ header: "Name", get: c => c.name, width: 30 }, { header: "Phone", get: c => c.phone || "" }, { header: "Email", get: c => c.email || "", width: 26 }, { header: "GSTIN", get: c => c.gstin || "", width: 18 }, { header: "Address", get: c => c.address || "", width: 30 }, { header: "City", get: c => c.city || "" }, { header: "State", get: c => c.state || "" },
      { header: "Opening Balance", get: c => n(c.opening_balance), num: true }, { header: "Current Balance", get: c => n(balances[c.id] ?? 0), num: true }, { header: "Status", get: c => c.active === false ? "Inactive" : "Active" }],
    rows: filtered, footer: ["Total", "", "", "", "", "", "", filtered.reduce((s,c)=>s+Number(c.opening_balance||0),0), filtered.reduce((s,c)=>s+Number(balances[c.id]||0),0), `${filtered.length} customers`] }]);

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{fontFamily: 'Plus Jakarta Sans'}}>Customers</h1>
          <p className="text-sm text-slate-500">Manage your customer master and ledger accounts.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input data-testid="customers-search" placeholder="Search name or phone" value={q} onChange={(e)=>setQ(e.target.value)}
            className="h-9 px-3 border border-slate-300 rounded-md text-sm bg-white outline-none focus:border-blue-600 flex-1 min-w-0 sm:w-56"/>
          <ExcelButton testid="customers-excel-btn" onClick={excel}/>
          <Can perm="import_export"><Link to="/data" data-testid="customers-import-link" className="h-9 px-3 border border-slate-300 bg-white rounded-md text-xs font-semibold uppercase tracking-wider flex items-center gap-2 hover:bg-slate-50"><Upload size={14}/>Import</Link></Can>
          <Can perm="vouchers"><button data-testid="add-customer-btn" onClick={()=>{setForm(EMPTY);setEditId(null);setOpen(true);}}
            className="bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold uppercase tracking-wider px-3.5 py-2 rounded-md flex items-center gap-2"><Plus size={14}/>Add</button></Can>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md table-scroll">
        <table className="acct-table">
          <thead><tr><th>Name</th><th>Phone</th><th>GSTIN</th><th className="num">Balance</th><th></th></tr></thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                <td><Link to={`/customers/${c.id}`} className="font-medium hover:underline">{c.name}</Link>{c.active === false && <span data-testid={`customer-inactive-${c.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">Inactive</span>}</td>
                <td className="mono text-slate-600">{c.phone}</td>
                <td className="mono text-slate-600">{c.gstin}</td>
                <td className={`num ${balances[c.id] > 0 ? "text-rose-800" : balances[c.id] < 0 ? "text-emerald-800" : "text-slate-500"}`}>{INR(balances[c.id] ?? 0)}</td>
                <td className="text-right whitespace-nowrap"><Can perm="vouchers">
                  <button data-testid={`customer-toggle-${c.id}`} onClick={()=>toggleActive(c)} title={c.active === false ? "Reactivate" : "Deactivate"} className="p-1.5 text-slate-500 hover:text-slate-900">{c.active === false ? <CheckCircle2 size={14}/> : <Ban size={14}/>}</button>
                  <button data-testid={`customer-edit-${c.id}`} onClick={()=>{setForm({...c});setEditId(c.id);setOpen(true);}} className="p-1.5 text-slate-500 hover:text-slate-900"><Pencil size={14}/></button>
                  <button data-testid={`customer-delete-${c.id}`} onClick={()=>del(c)} className="p-1.5 text-slate-500 hover:text-rose-700"><Trash2 size={14}/></button>
                </Can></td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan="5" className="text-center py-8 text-slate-400">No customers yet. Click <b>Add</b> to create one.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 grid place-items-center p-4" onClick={()=>setOpen(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg border border-slate-300 shadow-xl max-w-lg w-full p-5" data-testid="customer-form">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editId ? "Edit Customer" : "Add Customer"}</h3>
              <button type="button" onClick={()=>setOpen(false)} className="text-slate-400 hover:text-slate-900"><X size={18}/></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                ["name","Name*","text",true, "col-span-2"],
                ["phone","Phone","text",false,""],
                ["email","Email","email",false,""],
                ["gstin","GSTIN","text",false,""],
                ["opening_balance","Opening Balance ₹","number",false,""],
                ["credit_limit","Credit Limit ₹ (0 = no limit)","number",false,""],
                ["address","Address","text",false,"col-span-2"],
                ["city","City","text",false,""],
                ["state","State","text",false,""],
              ].map(([k,label,type,req,cn]) => (
                <div key={k} className={cn}>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</label>
                  <input data-testid={`customer-field-${k}`} type={type} required={req} value={form[k]??""}
                    onChange={(e)=>setForm({...form,[k]: type==="number"?parseFloat(e.target.value||0):e.target.value})}
                    className="mt-1 w-full h-9 px-2.5 border border-slate-300 rounded text-sm outline-none focus:border-blue-600"/>
                </div>
              ))}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={()=>setOpen(false)} className="h-9 px-4 border border-slate-300 rounded-md text-xs font-semibold uppercase tracking-wider bg-white hover:bg-slate-50">Cancel</button>
              <button type="submit" data-testid="customer-save-btn" className="h-9 px-4 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-xs font-semibold uppercase tracking-wider">{editId?"Update":"Save"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
